import React, { useEffect, useState } from "react";
import { getYears } from "../../../utils/date";
import { colorOptions, wineOrigins, SUBSCRIBERS_ONLY_HINT, lockScoreSortOptions } from "../../../utils/wine";
import { useValidSubscription } from "../../../hooks/useValidSubscription";
import Checkbox from "../Inputs/Checkbox";
import DropDown from "../Inputs/DropDown"
import FilterWrapper from "../Wrappers/FilterWrapper";
import OverflowWrapper from "../Wrappers/OverflowWrapper";
import { IFilter } from "../../global/Wines/WineFilters";
import {loadAuthors, useFilterContext} from "../../../context/filtersContext"

interface IBaseWineFiltersProps {
  additionalClassName?: string, 
  onApply?: Function,
  onSortChange: Function,
  onSortDirectionChange: Function,
  sortValue: string,
  sortDirection: string,
  values?: IFilter,
  setFilters?: (IFilter) => void,
  filters?: IFilter,
};

const BaseWineFilters = ({ 
  additionalClassName,
  onSortChange,
  onSortDirectionChange,
  sortValue,
  sortDirection,
  setFilters,
  filters,
  onApply,
}: IBaseWineFiltersProps) => {

  const [authorFilterOptions, setAuthorFilterOptions] = useState<Array<string>>()
  const hasValidSubscription = useValidSubscription();

  const sortLabels =  {
    "": "Default",
    vintage: "Vintage",
    producer_name: "Producer",
    wine_name: "Name",
    score: "Score",
    author: "Author",
    drinking_window_begin: "Drinking Window",
    review_date: "Review Date"
  }

  const sortOptions = React.useMemo(() => {
    let options = [];
    for (let key in sortLabels) {
      options.push({
        label: sortLabels[key],
        value: key
      })
    }

    return lockScoreSortOptions(options, !hasValidSubscription);
  }, [hasValidSubscription]);
  
  const { state: { authors }, dispatch: filtersDispatch } = useFilterContext();

  useEffect(()=>{
    loadAuthors(filtersDispatch)
  },[])


  useEffect(() => {
    const scopedAuthorFilterOptions = [];

    if (authors) {
      for (const key in authors) {
        scopedAuthorFilterOptions.push(authors[key]);
      }
    }

    setAuthorFilterOptions(scopedAuthorFilterOptions);
  }, [authors]);

  const handleFilterChange = (e, key) => {
    if(e.status) {
      setFilters({
        ...filters,
        [key]: e.value
      });
    } else if (filters[key]) {
      delete filters[key];
      setFilters({ ...filters});
    }
  }

  return (
    <div className={`bg-white rounded w-full fixed top-0 left-0 right-0 z-50 p-4 pb-20 overflow-scroll h-full lg:relative lg:overflow-visible lg:pb-0 lg:p-0 lg:z-10 lg:w-1/3 xl:w-1/4 align-start ${ additionalClassName }`}>
      <div className="sticky -top-4 lg:top-20 py-2 bg-white z-10 flex items-center justify-center">
        <h2 className="text-xl w-full">Filters</h2>
        <div className="flex">
          {
            Object.keys(filters).length > 0 &&
            <>
              <div 
                className="text-red cursor-pointer"
                onClick={() => {
                  setFilters({});
                }}
                onKeyDown={(e) => {if (e.key === "Enter") setFilters({})}}
                aria-label="Reset Filters"
                role="button"
                tabIndex={0}
              > 
                Reset
              </div>
              <div 
                className="pl-4 text-red cursor-pointer lg:hidden"
                onClick={() => {
                  onApply(filters)
                }}
                onKeyDown={(e) => {if (e.key === "Enter") onApply(filters)}}
                aria-label="Apply Filters"
                role="button"
                tabIndex={0}
              > 
                Apply
              </div>
            </>
          }
          <div 
            className="pl-4 text-red cursor-pointer lg:hidden"
            onClick={() => {
              onApply(filters);
            }}
            onKeyDown={(e) => {if (e.key === "Enter") onApply(filters)}}
            aria-label="Apply Filters"
            role="button"
            tabIndex={0}
          > 
            X
          </div>
          
        </div>
      </div>

      <FilterWrapper
        label="Sort By"
        flex
      >
        <DropDown
          type="line"
          options={sortOptions}
          value={sortLabels[sortValue]}
          onOptionSelect={(e) => {
            onSortChange(e);
          }}
        />
        <div
          className={`cursor-pointer hover:opacity-100`}
          onClick={() => {
            if (sortDirection === "desc") onSortDirectionChange("asc")
            if (sortDirection === "asc") onSortDirectionChange("desc")
          }}
          onKeyDown={(e) => {if (e.key === "Enter") {
            if (sortDirection === "desc") onSortDirectionChange("asc")
            if (sortDirection === "asc") onSortDirectionChange("desc")
          }}}
          aria-label="Change Sort Direction"
          role="button"
          tabIndex={0}
        >
          {
            sortDirection === "desc" ?
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
            </svg>
            :
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
            </svg>
          }
        </div>
      </FilterWrapper>

      <FilterWrapper
        label="Vintage Between"
        flex
      >
        <DropDown
          type="line"
          options={getYears(100)}
          placeholder="Min"
          value={filters.vintageYearMin}
          onOptionSelect={(e) => {
            setFilters({
              ...filters,
              vintageYearMin: e
            })
          }}
          className="text-gray-500"
        />
        <DropDown
          type="line"
          options={getYears(100)}
          placeholder="Max"
          value={filters.vintageYearMax}
          onOptionSelect={(e) => {
            setFilters({
              ...filters,
              vintageYearMax: e
            })
          }}
        />
      </FilterWrapper>

      <FilterWrapper
        label="Score Between"
        hint={hasValidSubscription ? undefined : SUBSCRIBERS_ONLY_HINT}
        flex
      >
        <DropDown
          type="line"
          options={Array.from({length: 100}, (_value, key) => (key + 1)).reverse()}
          placeholder="Min"
          disabled={!hasValidSubscription}
          value={filters.scoreBetweenMin}
          onOptionSelect={(e) => {
            setFilters({
              ...filters,
              scoreBetweenMin: parseInt(e)
            })
          }}
        />
        <DropDown
          type="line"
          options={Array.from({length: 100}, (_value, key) => (key + 1)).reverse()}
          placeholder="Max"
          disabled={!hasValidSubscription}
          value={filters.scoreBetweenMax}
          onOptionSelect={(e) => {
            setFilters({
              ...filters,
              scoreBetweenMax: parseInt(e)
            })
          }}
        />
      </FilterWrapper>

      <FilterWrapper
        label="Price Between"
        flex
      >
        <DropDown
          type="line"
          options={Array.from({length: 100}, (_value, key) => (key + 1) * 10)}
          placeholder="Min"
          value={filters.priceBetweenMin}
          onOptionSelect={(e) => {
            setFilters({
              ...filters,
              priceBetweenMin: e
            })
          }}
        />
        <DropDown
          type="line"
          options={Array.from({length: 100}, (_value, key) => (key + 1) * 10)}
          placeholder="Max"
          value={filters.priceBetweenMax}
          onOptionSelect={(e) => {
            setFilters({
              ...filters,
              priceBetweenMax: e
            })
          }}
        />
      </FilterWrapper>

      <FilterWrapper
        label="Review Date Between"
        flex
      >
        <DropDown
          type="line"
          options={getYears(100)}
          placeholder="Min"
          value={filters.reviewDateMin}
          onOptionSelect={(e) => {
            setFilters({
              ...filters,
              reviewDateMin: e
            })
          }}
        />
        <DropDown
          type="line"
          options={getYears(100)}
          placeholder="Max"
          value={filters.reviewDateMax}
          onOptionSelect={(e) => {
            setFilters({
              ...filters,
              reviewDateMax: e
            })
          }}
        />
      </FilterWrapper>

      <OverflowWrapper
        disableExpand={filters.country != undefined}
      >
        <FilterWrapper
          label="Country"
        >
          <div >
            {
              wineOrigins.filter((origin) => {
                return !filters.country || filters.country == origin
              }).map((origin) => {
                return (
                  <Checkbox
                    key={origin}
                    label={filters.country === origin ? origin + " (Remove)" : origin}
                    value={origin}
                    isActive={filters.country === origin}
                    onChange={(e) => {
                      handleFilterChange(e, "country");
                    }}
                  />
                )
              })
            }
          </div>
        </FilterWrapper>
      </OverflowWrapper>

      <OverflowWrapper
        disableExpand={filters.color != undefined}
      >
        <FilterWrapper
          label="Color"
        >
          <div >
            {
              colorOptions.filter((color) => {
                return !filters.color || filters.color === color
              }).map((color) => {
                return (
                  <Checkbox
                    key={color}
                    label={filters.color === color ? color + " (Remove)" : color}
                    value={color}
                    isActive={filters.color === color}
                    onChange={(e) => {
                      handleFilterChange(e, "color");
                    }}
                  />
                )
              })
            }
          </div>
        </FilterWrapper>
      </OverflowWrapper>

      <OverflowWrapper
        disableExpand={filters.color != undefined}
      >
        <FilterWrapper
          label="Author"
        >
          <div >
            {
              authorFilterOptions && authorFilterOptions.length > 0 && authorFilterOptions.filter((author) => {
                return !filters.author || filters.author === author
              }).map((author) => {
                return (
                  <Checkbox
                    key={author}
                    label={filters.author === author ? author + " (Remove)" : author}
                    value={author}
                    isActive={filters.author === author}
                    onChange={(e) => {
                      handleFilterChange(e, "author");
                    }}
                  />
                )
              })
            }
          </div>
        </FilterWrapper>
      </OverflowWrapper>
    </div>
  )
}

export default BaseWineFilters;
